// src/components/Questionnaire.jsx

import React, { useState, useEffect, Suspense, useRef } from 'react';
import {
    DndContext,
    closestCenter,
    useSensor,
    useSensors,
    TouchSensor,
    MouseSensor
} from '@dnd-kit/core';
import { SortableContext, arrayMove } from '@dnd-kit/sortable';
import './styles.scss';
import questions from './questions.json';
import Confetti from 'react-confetti';
import SortableItem from './SortableItem';

// Lazy load the ChartComponent
const ChartComponent = React.lazy(() => import('./ChartComponent'));

// Constants
const REPRESENTATIONAL_SYSTEMS = ["Visual", "Auditory", "Kinesthetic", "Auditory_Digital"];

// Utility Function to Initialize Data
const initializeData = (questions) => {
    return questions.map((question, qIndex) => ({
        id: `question-${qIndex}`,
        question: question.text || "Please respond to the following:",
        scores: question.options.map((option, cIndex) => ({
            id: `question-${qIndex}-option-${cIndex}`,
            option,
            mapping: question.mapping[cIndex],
            score: 4 - cIndex
        }))
    }));
};

// Calculation Function
const calculateResults = (data) => {
    const totals = {
        Visual: 0,
        Auditory: 0,
        Kinesthetic: 0,
        Auditory_Digital: 0
    };
    data.forEach(questionData => {
        questionData.scores.forEach(scoreData => {
            const { mapping, score } = scoreData;
            if (totals.hasOwnProperty(mapping)) {
                totals[mapping] += score;
            }
        });
    });
    return totals;
};

// ProgressBar Component
const ProgressBar = ({ current, total }) => {
    const percentage = ((current + 1) / total) * 100;
    return (
        <div className="progress-bar" aria-label={`Progress: ${percentage}%`}>
            <div className="progress-bar__progress" style={{ width: `${percentage}%` }}></div>
        </div>
    );
};

// NavigationButtons Component
const NavigationButtons = ({ onPrevious, onNext, onFinish, isFirst, isLast }) => {
    return (
        <div className="navigation-buttons">
            <button onClick={onPrevious} disabled={isFirst} aria-label="Previous Question">
                Previous
            </button>
            {isLast ? (
                <button onClick={onFinish} aria-label="Show Results">
                    Show Results
                </button>
            ) : (
                <button onClick={onNext} aria-label="Next Question">
                    Next
                </button>
            )}
        </div>
    );
};

// Results Component
const Results = ({ results, data, showResults, onPrint, onShare, onReset }) => {
    const resultsRef = useRef(null);

    useEffect(() => {
        if (showResults) {
            resultsRef.current?.scrollIntoView({ behavior: 'smooth' });
        }
    }, [showResults]);

    const maxLowScore = {
        max: REPRESENTATIONAL_SYSTEMS.length * 19,
        low: 19
    };

    const getPreferredSystems = () => {
        const maxScore = Math.max(...Object.values(results));
        return REPRESENTATIONAL_SYSTEMS.filter(system => results[system] === maxScore).join(", ");
    };

    return (
        <div ref={resultsRef} className={`results ${showResults ? 'visible' : ''}`} aria-live="polite">
            <h1 className="results__title">Results:</h1>
            {showResults && (
                <Suspense fallback={<div>Loading chart...</div>}>
                    <ChartComponent data={{
                        labels: REPRESENTATIONAL_SYSTEMS,
                        datasets: [
                            {
                                label: 'Scores',
                                data: REPRESENTATIONAL_SYSTEMS.map(system => results[system]),
                                backgroundColor: REPRESENTATIONAL_SYSTEMS.map(system => getColor(system)),
                                borderColor: REPRESENTATIONAL_SYSTEMS.map(system => getColor(system, 'dark')),
                                borderWidth: 2,
                            },
                        ],
                    }} />
                </Suspense>
            )}
            <div className="results__max-low-score">
                <p><strong>Maximum score per Representational System:</strong> {maxLowScore.max}</p>
                <p><strong>Lowest score per Representational System:</strong> {maxLowScore.low}</p>
                <p>
                    <strong>Preferred Representational System: </strong>
                    <span className="result-label">{getPreferredSystems()}</span>
                </p>
            </div>
            <div>
                <h2>Scores Analysis Reference</h2>
                <table className="results__analysis-table">
                    <thead>
                        <tr>
                            <th>Q. Number</th>
                            {REPRESENTATIONAL_SYSTEMS.map(system => (
                                <th key={system}>{system.replace('_', ' ')}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {data.map((questionData, qIndex) => (
                            <tr key={questionData.id}>
                                <td>{qIndex + 1}</td>
                                {REPRESENTATIONAL_SYSTEMS.map(system => (
                                    <td key={system} className={system.toLowerCase().replace('_', '-')}>
                                        {questionData.scores.find(score => score.mapping === system)?.score || ''}
                                    </td>
                                ))}
                            </tr>
                        ))}
                        <tr className='total'>
                            <td>TOTAL</td>
                            {REPRESENTATIONAL_SYSTEMS.map(system => (
                                <td key={system}>{results[system]}</td>
                            ))}
                        </tr>
                    </tbody>
                </table>
            </div>
            <div className="results__btn-group">
                <button className="btn" onClick={onPrint} aria-label="Print Results">Print</button>
                <button className="btn" onClick={onShare} aria-label="Share Results">Share</button>
            </div>
            <div className="btn-reset">
                <button className="btn" onClick={onReset} aria-label="Share Results">Reset</button>
            </div>
        </div>
    );
};

// ShowResultsButton Component
const ShowResultsButton = ({ onToggle, isVisible }) => (
    <div className="show-results-div">
        <button className="show-results-div__button" onClick={onToggle} aria-expanded={isVisible}>
            {isVisible ? 'Hide Results' : 'Show Results'}
        </button>
    </div>
);

// Utility to Get Color
const getColor = (type, variant = 'default') => {
    const rootStyles = getComputedStyle(document.documentElement);
    const colorMap = {
        'Visual': {
            default: rootStyles.getPropertyValue('--color-visual').trim(),
            dark: rootStyles.getPropertyValue('--color-visual-dark').trim()
        },
        'Auditory': {
            default: rootStyles.getPropertyValue('--color-auditory').trim(),
            dark: rootStyles.getPropertyValue('--color-auditory-dark').trim()
        },
        'Kinesthetic': {
            default: rootStyles.getPropertyValue('--color-kinesthetic').trim(),
            dark: rootStyles.getPropertyValue('--color-kinesthetic-dark').trim()
        },
        'Auditory_Digital': {
            default: rootStyles.getPropertyValue('--color-auditory-digital').trim(),
            dark: rootStyles.getPropertyValue('--color-auditory-digital-dark').trim()
        }
    };
    return colorMap[type][variant];
};

// Introduction Component
const Introduction = ({ onStart }) => {
    return (
        <div className="section--instructions">
            <h2 className="heading__title">Welcome to Alta Training & Coaching RSP Test</h2>
            <p>This test is designed to help you identify your preferred learning and communication styles. By understanding whether you lean more towards visual, auditory, kinesthetic, or auditory-digital preferences, you can enhance your personal and professional interactions.</p>
            <p>Throughout the test, you'll be presented with a series of statements. Please allocate a number to each statement based on how well it describes you.</p>
            <div style={{ textAlign: 'center' }}>
                <button onClick={onStart} className="show-results-div__button" aria-label="Start Test">
                    Start Test
                </button>
            </div>
        </div>
    );
};

// Main Questionnaire Component
const Questionnaire = () => {
    const [data, setData] = useState(() => initializeData(questions));
    const [results, setResults] = useState({
        Visual: 0,
        Auditory: 0,
        Kinesthetic: 0,
        Auditory_Digital: 0
    });
    const [showResults, setShowResults] = useState(false);
    const [currentQuestion, setCurrentQuestion] = useState(0);
    const [showConfetti, setShowConfetti] = useState(false);
    const [currentPage, setCurrentPage] = useState('intro'); // 'intro' or 'questionnaire'

    const resultsRef = useRef(null);

    const sensors = useSensors(
        useSensor(MouseSensor),
        useSensor(TouchSensor, {
            activationConstraint: {
                delay: 20,
                tolerance: 1,
            },
        })
    );

    useEffect(() => {
        const newResults = calculateResults(data);
        setResults(newResults);
    }, [data]);

    useEffect(() => {
        if (showResults) {
            setShowConfetti(true);
        }
    }, [showResults]);

    const handleConfettiComplete = () => {
        resultsRef.current?.scrollIntoView({ behavior: 'smooth' });
        setShowConfetti(false);
    };

    const handleDragEnd = (event) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;

        setData((prevData) => {
            const newData = [...prevData];
            const questionIndex = newData.findIndex(q => q.scores.some(s => s.id === active.id));
            if (questionIndex === -1) return newData;
            const activeIndex = newData[questionIndex].scores.findIndex(s => s.id === active.id);
            const overIndex = newData[questionIndex].scores.findIndex(s => s.id === over.id);
            if (overIndex === -1) return newData;

            newData[questionIndex].scores = arrayMove(newData[questionIndex].scores, activeIndex, overIndex).map((score, index) => ({
                ...score,
                score: 4 - index
            }));
            return newData;
        });
    };

    const handleShowResultsToggle = () => {
        setShowResults(prev => !prev);
    };

    const handleNext = () => {
        if (currentQuestion < data.length - 1) {
            setCurrentQuestion(prev => prev + 1);
        }
    };

    const handlePrevious = () => {
        if (currentQuestion > 0) {
            setCurrentQuestion(prev => prev - 1);
        }
    };

    const handleFinish = () => {
        setShowResults(true);
    };

    const handlePrint = () => {
        window.print();
    };

    const handleShare = () => {
        if (navigator.share) {
            navigator.share({
                title: 'Representational System Preference Test Results',
                text: 'Check out my Representational System Preference Test results!',
                url: window.location.href,
            })
                .catch((error) => console.log('Error sharing:', error));
        } else {
            alert('Share API is not supported in your browser.');
        }
    };

    const handleReset = () => {
        setData(initializeData(questions));
        setCurrentQuestion(0);
        setShowResults(false);
        setCurrentPage('intro');
    };

    const handleStart = () => {
        setCurrentPage('questionnaire');
    };

    return (
        <div>
            <header className="header">
                <h1 className="header__title">Representational System Preference Test</h1>
            </header>

            {currentPage === 'intro' && (
                <Introduction onStart={handleStart} />
            )}

            {currentPage === 'questionnaire' && !showResults && (
                <>
                    <ProgressBar current={currentQuestion} total={data.length} />
                    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                        <SortableContext items={data[currentQuestion].scores.map(score => score.id)}>
                            <div key={data[currentQuestion].id}>
                                <h4 className="heading__title">{`${currentQuestion + 1}. ${data[currentQuestion].question}`}</h4>
                                <div className="row">
                                    {data[currentQuestion].scores.map((scoreData, index) => (
                                        <div className="row__option" key={scoreData.id}>
                                            <div className="row__option__priority-number">
                                                {4 - index}
                                            </div>
                                            <SortableItem
                                                id={scoreData.id}
                                                option={scoreData.option}
                                                mapping={scoreData.mapping}
                                                isShowColors={showResults}
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </SortableContext>
                    </DndContext>
                    <NavigationButtons
                        onPrevious={handlePrevious}
                        onNext={handleNext}
                        onFinish={handleFinish}
                        isFirst={currentQuestion === 0}
                        isLast={currentQuestion === data.length - 1}
                    />
                </>
            )}

            {currentPage === 'questionnaire' && showResults && (
                <>
                    <Confetti
                        numberOfPieces={500}
                        recycle={false}
                        run={showConfetti}
                        onConfettiComplete={handleConfettiComplete}
                        style={{
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            pointerEvents: 'none',
                            zIndex: 9999,
                        }}
                    />
                    <ShowResultsButton onToggle={handleShowResultsToggle} isVisible={showResults} />
                    <Results
                        results={results}
                        data={data}
                        showResults={showResults}
                        onPrint={handlePrint}
                        onShare={handleShare}
                        onReset={handleReset}
                    />
                </>
            )}

            <footer className="footer">
                <p>&copy; {new Date().getFullYear()}  <a href="https://www.dralirezasharifi.com" target="_blank" rel="noopener noreferrer">dralirezasharifi.com</a></p>
                <p>Designed and developed by <a href="https://www.xesix.com" target="_blank" rel="noopener noreferrer">Xesix Inc.</a></p>
            </footer>
        </div>
    )
};

export default Questionnaire;
